import { ASSETS_WORKSPACE_HOST_TTL_MS } from '../common/constants';
import {
	AssetsWorkspaceHostStatus,
	type AssetsWorkspaceHost,
	type AssetsWorkspaceHostState,
	type UnitsEnabledCheck,
} from '../common/types';

/**
 * Module-scoped, in-memory store for resolved Assets workspace coordinates,
 * keyed by `cloudId`.
 *
 * Why module scope (and not React context):
 *  - Many independent React trees on a Confluence page can share a single
 *    cache miss (e.g. a smart-link list of 50 Assets cards collapses to 1
 *    network call) without any cross-tree wiring.
 *  - Mirrors the precedent set by `@atlaskit/smart-card`'s store, which
 *    Confluence already runs at scale.
 *
 * Why no persistence:
 *  - The host can change when a tenant's Units topology changes (e.g. a JSM
 *    licence is removed mid-session). We must never serve stale host across
 *    a full reload or login. In-memory storage gives us that for free.
 *  - Cache key is `cloudId`; tenant switch within an SPA is therefore an
 *    automatic cache miss.
 */

// Units check result, kept apart from `state` so callers that opt out never see it.
type UnitsCheck = {
	readonly enabled: boolean | null;
	readonly multiSite: boolean | null;
	readonly expiresAt: number;
	readonly inFlight: Promise<UnitsEnabledCheck> | null;
};

type CacheEntry = {
	readonly inFlight: Promise<AssetsWorkspaceHost> | null;
	readonly resolvedAt: number;
	readonly state: AssetsWorkspaceHostState;
	readonly units: UnitsCheck;
};

const NO_UNITS_CHECK: UnitsCheck = { enabled: null, multiSite: null, expiresAt: 0, inFlight: null };

const cache: Map<string, CacheEntry> = new Map();
const subscribers: Map<string, Set<() => void>> = new Map();

const PENDING_STATE: AssetsWorkspaceHostState = {
	status: AssetsWorkspaceHostStatus.Pending,
	data: null,
	error: null,
};

const IDLE_STATE: AssetsWorkspaceHostState = {
	status: AssetsWorkspaceHostStatus.Idle,
	data: null,
	error: null,
};

const notify = (cloudId: string): void => {
	const listeners = subscribers.get(cloudId);
	if (!listeners) {
		return;
	}
	listeners.forEach((listener) => {
		listener();
	});
};

export const subscribe = (cloudId: string, listener: () => void): (() => void) => {
	let listeners = subscribers.get(cloudId);
	if (!listeners) {
		listeners = new Set();
		subscribers.set(cloudId, listeners);
	}
	listeners.add(listener);
	return () => {
		listeners?.delete(listener);
		if (listeners && listeners.size === 0) {
			subscribers.delete(cloudId);
		}
	};
};

export const getSnapshot = (cloudId: string): AssetsWorkspaceHostState => {
	const entry = cache.get(cloudId);
	return entry ? entry.state : IDLE_STATE;
};

/**
 * SSR snapshot: always pending. We never let the slow resolver block the
 * server-rendered HTML — consumers render an optimistic same-site link on
 * the server and upgrade it after hydration.
 */
export const getServerSnapshot = (): AssetsWorkspaceHostState => PENDING_STATE;

export const isStale = (cloudId: string, now: number = Date.now()): boolean => {
	const entry = cache.get(cloudId);
	if (!entry || entry.state.status !== AssetsWorkspaceHostStatus.Success) {
		return true;
	}
	return now - entry.resolvedAt > ASSETS_WORKSPACE_HOST_TTL_MS;
};

export const getInFlight = (cloudId: string): Promise<AssetsWorkspaceHost> | null => {
	return cache.get(cloudId)?.inFlight ?? null;
};

export const setPending = (cloudId: string, inFlight: Promise<AssetsWorkspaceHost>): void => {
	const previous = cache.get(cloudId);
	cache.set(cloudId, {
		// Preserve the previously-cached success value (if any) so consumers
		// keep rendering the last-known answer while a background refresh is
		// in flight. Only switch to `pending` on a true cold start.
		state:
			previous && previous.state.status === AssetsWorkspaceHostStatus.Success
				? previous.state
				: PENDING_STATE,
		resolvedAt: previous?.resolvedAt ?? 0,
		inFlight,
		units: previous?.units ?? NO_UNITS_CHECK,
	});
	notify(cloudId);
};

export const setSuccess = (
	cloudId: string,
	data: AssetsWorkspaceHost,
	now: number = Date.now(),
): void => {
	cache.set(cloudId, {
		state: { status: AssetsWorkspaceHostStatus.Success, data, error: null },
		resolvedAt: now,
		inFlight: null,
		units: cache.get(cloudId)?.units ?? NO_UNITS_CHECK,
	});
	notify(cloudId);
};

export const setError = (cloudId: string, error: Error): void => {
	cache.set(cloudId, {
		state: { status: AssetsWorkspaceHostStatus.Error, data: null, error },
		resolvedAt: Date.now(),
		inFlight: null,
		units: cache.get(cloudId)?.units ?? NO_UNITS_CHECK,
	});
	notify(cloudId);
};

export const setUnavailable = (cloudId: string): void => {
	cache.set(cloudId, {
		state: { status: AssetsWorkspaceHostStatus.Unavailable, data: null, error: null },
		resolvedAt: Date.now(),
		inFlight: null,
		units: cache.get(cloudId)?.units ?? NO_UNITS_CHECK,
	});
	notify(cloudId);
};

const setUnitsCheck = (cloudId: string, units: UnitsCheck): void => {
	const previous = cache.get(cloudId);
	cache.set(cloudId, {
		state: previous?.state ?? IDLE_STATE,
		resolvedAt: previous?.resolvedAt ?? 0,
		inFlight: previous?.inFlight ?? null,
		units,
	});
};

export const getUnitsEnabled = (cloudId: string): boolean | null =>
	cache.get(cloudId)?.units.enabled ?? null;

export const getMultiSite = (cloudId: string): boolean | null =>
	cache.get(cloudId)?.units.multiSite ?? null;

export const getServerMultiSite = (): boolean | null => null;

export const getUnitsExpiresAt = (cloudId: string): number | null => {
	const units = cache.get(cloudId)?.units;
	return units && units.enabled !== null ? units.expiresAt : null;
};

export const getServerUnitsExpiresAt = (): number | null => null;

export const getServerUnitsEnabled = (): boolean | null => null;

export const isUnitsEnabledStale = (cloudId: string, now: number = Date.now()): boolean => {
	const units = cache.get(cloudId)?.units;
	return !units || units.enabled === null || now >= units.expiresAt;
};

export const getUnitsInFlight = (cloudId: string): Promise<UnitsEnabledCheck> | null =>
	cache.get(cloudId)?.units.inFlight ?? null;

export const setUnitsInFlight = (cloudId: string, inFlight: Promise<UnitsEnabledCheck>): void => {
	const previous = cache.get(cloudId)?.units ?? NO_UNITS_CHECK;
	// Keep the last-known answer visible while a refresh is in flight.
	setUnitsCheck(cloudId, { ...previous, inFlight });
};

export const setUnitsEnabled = (
	cloudId: string,
	enabled: boolean,
	ttlMs: number,
	multiSite: boolean | null = null,
	now: number = Date.now(),
): void => {
	setUnitsCheck(cloudId, { enabled, multiSite, expiresAt: now + ttlMs, inFlight: null });
	notify(cloudId);
};

/**
 * Clear cached value(s). Pass a `cloudId` to invalidate one tenant; omit to
 * invalidate every tenant in this tab (e.g. on logout). Notifies subscribers
 * so any mounted hooks immediately re-render to `idle`/`pending`.
 */
export const clearAssetsWorkspaceHostCache = (cloudId?: string): void => {
	if (cloudId === undefined) {
		const keys = Array.from(cache.keys());
		cache.clear();
		keys.forEach(notify);
		return;
	}
	cache.delete(cloudId);
	notify(cloudId);
};

/** @internal Test-only reset hook. */
export const __resetForTests = (): void => {
	cache.clear();
	subscribers.clear();
};
